/* @flow */
import { observable } from 'mobx';
import * as React from 'react';
import { Link } from 'react-router-dom';

import NavigationService from 'services/NavigationService';
import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import { createNewProject } from 'permissions/project';
import ManageUserLoginView from 'components/ManageUserLoginView/ManageUserLoginView';

import { formattedDate } from '../components/fieldComponents';
import createReportView from '../components/ReportView/ReportViewBase';
import type {
  ViewConfig,
  CellComponentProps,
  DataViewColumn,
} from '../components/ReportView/types';
import { actionsListSingle, actionsListMultiple } from './actions';

const IdLink: React.StatelessComponent<CellComponentProps> = ({ display, rowData }) => (
  <Link to={`/projects/${rowData.id}`}>{display}</Link>
);

const columns: DataViewColumn[] = [
  {
    columnName: 'code',
    title: 'general:projects.labels.code',
    flexBasis: 200,
    component: IdLink,
  },
  {
    columnName: 'name',
    title: 'general:projects.labels.name',
    flexBasis: 240,
    component: IdLink,
  },
  {
    columnName: 'description',
    title: 'general:description',
    flexBasis: 240,
  },
  {
    columnName: 'start_date',
    title: 'general:labels.start_date',
    flexBasis: 200,
    component: formattedDate('DD-MM-YYY'),
  },
  {
    columnName: 'end_date',
    title: 'general:labels.end_date',
    flexBasis: 200,
    component: formattedDate('DD-MM-YYY'),
  },
  {
    columnName: 'cost_centre_count',
    title: 'general:cost_centers.labels.multiple_assigned',
    flexBasis: 160,
    component: function CostCenterAssignedCell({ display, rowData }) {
      if (display === 0) {
        return <span>{display}</span>;
      }

      return (
        <Link
          to={NavigationService.formulateReportViewUri('/cost-centers', {
            page: 1,
            filters: [{ key: 'project', values: [rowData.id] }],
          })}
        >
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'engineer_count',
    title: 'general:engineers.labels.assigned',
    flexBasis: 160,
    component: function EngineersAssignedCell({ display, rowData }) {
      if (display === 0) {
        return <span>{display}</span>;
      }

      return (
        <Link to={`/projects/${rowData.id}`}>
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'assigned_assets_count',
    title: 'general:cost_centers.labels.assets_assigned',
    flexBasis: 160,
    component: function AssetAssignedCell({ display, rowData }) {
      if (display === 0) {
        return <span>{display}</span>;
      }

      return (
        <Link
          to={NavigationService.formulateReportViewUri('/assets', {
            page: 1,
            filters: [
              { key: 'project', values: [rowData.id] },
              { key: 'asset_type', values: ['Rented Open'] },
              { key: 'asset_type', values: ['Owned'] },
              { key: 'asset_type', values: ['Purchased'] },
            ],
          })}
        >
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'project_lead',
    title: 'general:projects.labels.lead',
    flexBasis: 200,
    component: function ProjectLeadCell({ display }) {
      return (
        <span>
          {display && display.first_name} {display && display.last_name}
        </span>
      );
    },
  },
  {
    columnName: 'status',
    title: 'general:labels.status',
    flexBasis: 200,
    component: function ProjectStatusCell({ display }) {
      return display && <StatusIndicator status={display} />;
    },
  },
];

const viewConfig: ViewConfig = {
  title: 'general:project_plural',
  codename: 'projects',
  type: 'SINGLE',
  userFilterNamespace: 'Projects',
  view: {
    columns,
    idKey: 'id',
    dataPathname: 'projects',
    apiType: 'SQL',
    actions: {
      single: actionsListSingle,
      multi: actionsListMultiple,
    },
  },
  buttons: [
    {
      label: 'general:actions.new',
      icon: '#icon-add',
      to: '/projects/create',
      permissions: createNewProject,
    },
  ],
};

export default createReportView(viewConfig);
